// ============================================================================
// GrilleSalarialeEditPage — editeur de la grille salariale CE (L06, GC-C)
// ============================================================================
// Port du PoC app/(dashboard)/grille-salariale/edit/page.tsx (SSR) au standard
// maison (SPA). BackLink -> /grille-salariale ; section « Grilles annuelles »
// (M-677/M-679) : bouton Initialiser (migrate) si AUCUNE annee figee, sinon bouton
// Creer N+1 (rollover from latestYear) ; puis SalaryGridEditor (master).
//
// GARDE M-670 (P-CE14) DOUBLEE CLIENT (comme GC-B) : la garde REELLE est le 403
// SERVEUR (admin SEUL ; director/manager/employee refuses par requireSalaryGridCE).
// La query GET / 403 pour un non-admin -> ApiForbiddenError -> /error/403. Le SPA
// ne DECIDE pas (GA-05) ; il double la garde serveur.
//
// P-CE2 (operations de masse) : migrate/rollover derriere une CONFIRMATION 2 temps
// (ConfirmDialog) + boutons `isLoading`/`disabled` pendant la mutation (double-clic
// bloque cote UI ; l'idempotence serveur — year_exists relu dans la tx — est le
// vrai garde-fou). save/delete de classe = TOAST (dans SalaryGridEditor) ;
// loadDefault/rollover/migrate = bandeau ?ok= (VERBATIM PoC redirect).
//
// [!] LECTURE MASTER — FERMEE (fiche [GEST-GRILLE-GET-MASTER-AVANT-MIGRATION]) :
// la page lit desormais GET /master via useGrilleCeMaster (grille MASTER seule,
// hors resolution annuelle). L'editeur RELIT donc ce qu'il ECRIT (saveSalaryClass,
// grid_scope='master'), meme apres qu'une annee ait ete figee (migrateAnnualGrids).
// `gridYears` (annees figees) est inclus dans le bundle /master.

import { useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import {
  PageHeader, EmptyState, SkeletonCard, QueryError, ConfirmDialog, GS_COMPONENTS,
} from '@gs/ui';
import { ArrowLeft, CalendarPlus, History, Table2 } from 'lucide-react';
import { ApiForbiddenError } from '@gs/api-client';
import { useAuth } from '@gs/auth';
import { useTranslation } from '../../i18n';
import {
  useGrilleCeMaster, useRolloverGrid, useMigrateAnnualGrids, gridEditErrorCode,
} from '../../hooks/useGrilleCe';
import { adaptGridClasses } from '../../hooks/usePlanification';
import { SalaryGridEditor } from '../../components/salary/SalaryGridEditor';

/** Codes ?ok= de la page d'edition (VERBATIM PoC OK_LABEL). */
const OK_CODES = ['loaded', 'migrated', 'rolled'] as const;

export default function GrilleSalarialeEditPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const okCode = searchParams.get('ok');
  const errorCode = searchParams.get('error');

  // Bundle EDITEUR (GET /master) — master seule, robuste apres migrateAnnualGrids.
  const q = useGrilleCeMaster(!!user);
  const rollover = useRolloverGrid();
  const migrate = useMigrateAnnualGrids();

  // Confirmation 2 temps (P-CE2) : quelle operation de masse est en attente.
  const [confirmOp, setConfirmOp] = useState<null | 'migrate' | 'rollover'>(null);

  // Garde CLIENT M-670 : 403 serveur (non-admin) -> ecran d'erreur standard (double
  // la garde serveur, GA-05).
  if (q.isError && q.error instanceof ApiForbiddenError) {
    return <Navigate to="/error/403" replace />;
  }

  const data = q.data;
  const classes = data ? adaptGridClasses(data.classes) : [];
  const gridYears = data?.gridYears ?? [];
  // latestYear = plus recente annee figee (VERBATIM PoC years[0], robuste au tri).
  const latestYear = gridYears.length > 0 ? Math.max(...gridYears) : null;

  function goBanner(kind: 'ok' | 'error', code: string) {
    const next = new URLSearchParams(searchParams);
    next.delete('ok');
    next.delete('error');
    next.set(kind, code);
    setSearchParams(next, { replace: true });
  }

  function runMigrate() {
    migrate.mutate(undefined, {
      onSuccess: () => { setConfirmOp(null); goBanner('ok', 'migrated'); },
      onError: (err) => { setConfirmOp(null); goBanner('error', gridEditErrorCode(err)); },
    });
  }
  function runRollover() {
    if (latestYear === null) return;
    rollover.mutate(latestYear, {
      onSuccess: () => { setConfirmOp(null); goBanner('ok', 'rolled'); },
      onError: (err) => { setConfirmOp(null); goBanner('error', gridEditErrorCode(err)); },
    });
  }

  const annualAction =
    latestYear === null ? (
      <button
        type="button"
        onClick={() => setConfirmOp('migrate')}
        disabled={migrate.isPending}
        className={`${GS_COMPONENTS.btnAction} inline-flex touch-manipulation items-center gap-2 disabled:opacity-50`}
      >
        <History size={16} aria-hidden="true" />
        {t('gest:grilleCe.editPage.annual.migrate')}
      </button>
    ) : (
      <button
        type="button"
        onClick={() => setConfirmOp('rollover')}
        disabled={rollover.isPending}
        className={`${GS_COMPONENTS.btnSecondary} inline-flex touch-manipulation items-center gap-2 disabled:opacity-50`}
      >
        <CalendarPlus size={16} aria-hidden="true" />
        {t('gest:grilleCe.editPage.annual.rollover', { year: latestYear + 1 })}
      </button>
    );

  return (
    <div className="space-y-6">
      {/* Fil de retour (destination fixe : la grille CE). */}
      <nav aria-label={t('gest:grilleCe.editPage.back')} className="flex items-center">
        <Link
          to="/grille-salariale"
          className="inline-flex touch-manipulation items-center gap-1.5 text-meta font-medium text-slate-500 transition-colors hover:text-slate-700 dark:hover:text-slate-300"
        >
          <ArrowLeft size={16} aria-hidden="true" />
          {t('gest:grilleCe.editPage.back')}
        </Link>
      </nav>

      <PageHeader
        title={t('gest:grilleCe.editPage.title')}
        description={t('gest:grilleCe.editPage.description')}
        onRefresh={() => void q.refetch()}
        refreshLoading={q.isFetching}
      />

      {/* Bandeau ?ok= (retour de loadDefault/rollover/migrate, deep-linkable). */}
      {okCode && (OK_CODES as readonly string[]).includes(okCode) ? (
        <div
          role="status"
          className="rounded-xl border-l-4 border-green-500 bg-green-50 p-4 text-field text-green-700 dark:bg-green-950 dark:text-green-300"
        >
          {t(`gest:grilleCe.editOk.${okCode}`, { defaultValue: t('gest:grilleCe.editOk.loaded') })}
        </div>
      ) : null}

      {/* Bandeau ?error= (codes courts P-CE16 : year_exists / empty_master / ...). */}
      {errorCode ? (
        <div
          role="alert"
          className="rounded-xl border-l-4 border-red-500 bg-red-50 p-4 text-field text-red-700 dark:bg-red-950 dark:text-red-300"
        >
          {t(`gest:grilleCe.editErrors.${errorCode}`, { defaultValue: t('gest:grilleCe.editErrors.server') })}
        </div>
      ) : null}

      {q.isLoading ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <SkeletonCard />
          <SkeletonCard />
        </div>
      ) : q.isError ? (
        <QueryError message={t('gest:grilleCe.editPage.loadError')} onRetry={() => void q.refetch()} />
      ) : data ? (
        <>
          {/* Grilles annuelles : initialisation (migrate) puis rollover N+1. */}
          <section className={`${GS_COMPONENTS.card} p-5`}>
            <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
              <div className="flex flex-col gap-0.5">
                <span className="text-chip font-medium uppercase tracking-wide text-slate-400">
                  {t('gest:grilleCe.editPage.annual.eyebrow')}
                </span>
                <h2 className="text-body font-semibold text-slate-800 dark:text-slate-100">
                  {t('gest:grilleCe.editPage.annual.title')}
                </h2>
              </div>
              {annualAction}
            </div>
            <p className="text-field text-slate-500 dark:text-slate-400">
              {latestYear === null
                ? t('gest:grilleCe.editPage.annual.descNone')
                : t('gest:grilleCe.editPage.annual.descSome', {
                    years: [...gridYears].sort((a, b) => a - b).join(', '),
                  })}
            </p>
          </section>

          {classes.length === 0 ? (
            <EmptyState
              icon={<Table2 size={48} aria-hidden="true" />}
              title={t('gest:grilleCe.editor.empty.title')}
              description={t('gest:grilleCe.editor.empty.description')}
            />
          ) : null}

          <SalaryGridEditor classes={classes} />
        </>
      ) : null}

      {/* P-CE2 : confirmation 2 temps des operations de masse (idempotence serveur
          en filet ; l'UI empeche le double-clic via isLoading/disabled). */}
      <ConfirmDialog
        open={confirmOp === 'migrate'}
        variant="warning"
        title={t('gest:grilleCe.editPage.confirm.migrate.title')}
        message={t('gest:grilleCe.editPage.confirm.migrate.message')}
        confirmLabel={t('gest:grilleCe.editPage.annual.migrate')}
        cancelLabel={t('gest:grilleCe.editor.cancel')}
        isLoading={migrate.isPending}
        onConfirm={runMigrate}
        onCancel={() => setConfirmOp(null)}
      />
      <ConfirmDialog
        open={confirmOp === 'rollover'}
        variant="warning"
        title={t('gest:grilleCe.editPage.confirm.rollover.title', { year: (latestYear ?? 0) + 1 })}
        message={t('gest:grilleCe.editPage.confirm.rollover.message', { year: (latestYear ?? 0) + 1 })}
        confirmLabel={t('gest:grilleCe.editPage.annual.rollover', { year: (latestYear ?? 0) + 1 })}
        cancelLabel={t('gest:grilleCe.editor.cancel')}
        isLoading={rollover.isPending}
        onConfirm={runRollover}
        onCancel={() => setConfirmOp(null)}
      />
    </div>
  );
}
